import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Subject, debounceTime } from 'rxjs';
import { WebsocketData, WSService } from 'src/app/core/_services/ws.service';
import { ThemeService } from 'src/app/core/_services/theme.service';
import { OrgThemeService, ThemeSetting } from 'src/app/core/_services/orgTheme.service';
import { SessionService as CoreSessionService } from 'src/app/core/_services/session.service';
import { DeviceProfilePayload } from 'src/app/core/_interface/deviceProfile';
import { TaxCoreService } from 'src/app/core/_services/taxCore.service';
import { CompanySettings } from 'src/app/core/_interface/company';
import { OrderStatus } from 'src/app/core/_interface/order';

export enum Destination {
  Tables = 0,
  CurrentTable,
  Companies,
  Logout
}
export enum BUTTON_SIZE {
  DEFAULT = 0,
  SMALL = -1
}

/** Kako se prikazuju stolovi koje konobar NIJE zadužio (stolovi kolega). */
export enum MyTablesDisplay {
  /** Svi stolovi, bez ikakve razlike (default). */
  All = 'all',
  /** Svi stolovi, ali su stolovi zaduženog konobara dodatno istaknuti. */
  Highlight = 'highlight',
  /** Tuđi stolovi se sakrivaju — prikazuju se samo stolovi zaduženog konobara. */
  OnlyMine = 'onlyMine'
}

/**
 * Statusi direktnih porudžbina koji su se i do sada prikazivali (skup koji učitava core
 * OrderService.getActiveInstantOrders, bez zastarelog Neispravna — takve stare porudžbine se
 * prikazuju kao Nova) — podrazumevani izbor i skup koji se uvek prati.
 * Redosled prati enum (kao što ga emituje app-enum-checkbox-selector): save() i profil porede
 * nizove kroz JSON.stringify, pa bi drugi redosled isti izbor video kao lokalnu izmenu.
 */
export const DEFAULT_INSTANT_ORDER_STATUSES: readonly OrderStatus[] = [
  OrderStatus.New,
  OrderStatus.Accepted,
  OrderStatus.InProduction,
  OrderStatus.Ready,
  OrderStatus.NewPreorder,
  OrderStatus.Pending,
];

export class Settings {
  printOrderNo = false;
  printOrderConfirmation = false;
  /** Fiskalizacija preko PFX-a (VPFR): konobar lokalno potpisuje i štampa fiskalni račun. */
  generateInvoiceWithVPFR = false;
  extraTextForUnpaidOrders = "";

  onOrderCreatedDestination: Destination = Destination.Tables;
  showPhoneCallNotifications = false;
  buttonSize = BUTTON_SIZE.DEFAULT;
  /** Najviše redova grupa iznad artikala (1–5); null = automatski (telefon 1, širok ekran 5).
   *  Inicijalizacija na null je obavezna (v. komentar kod showOrderNoOnCreate). */
  groupRows: number | null = null;
  /** Sve grupe u traci visine `groupRows` redova, skrol gore-dole — bez dugmeta za sve grupe. */
  groupsScroll = false;
  autoLogoutTime: number = 0;
  autoCloseTableTime: number = 0;
  useGridTableView: boolean = false;
  layoutTableView: 'cover' | 'contain' = 'cover';
  autoRotateView: boolean = false;
  /** Prikaz stolova kolega: svi (default) / svi uz isticanje mojih / samo moji. */
  myTablesDisplay: MyTablesDisplay = MyTablesDisplay.All;
  selectedGroupName: string | null = null;
  enableZoom = true;
  onCompanyOpenedDestination = false;
  logPing = false;

  // Podešavanja koja uređaj nasleđuje sa servera (vidi SERVER_SETTING): null = „Koristi podešavanje
  // sa servera“, true/false = izbor na samom uređaju (jači od servera). Inicijalizacija na null je
  // obavezna — bez nje (useDefineForClassFields:false) ključa nema u Object.keys(new Settings()),
  // pa ga ni forma na ekranu podešavanja ne bi dobila.
  /** Obaveštenje sa brojem porudžbine posle kreiranja. */
  showOrderNoOnCreate: boolean | null = null;
  /** Ponavljaj zvuk dok postoje direktne porudžbine koje zahtevaju potvrdu. */
  repeatSoundForPendingOrders: boolean | null = null;
  /** Naplaćene direktne porudžbine ostaju u listi. */
  showPaidInstantOrders: boolean | null = null;

  /** Statusi u kojima se direktne porudžbine prikazuju (kao visibleOrderStatuses na KD-u). */
  visibleInstantOrderStatuses: OrderStatus[] = [...DEFAULT_INSTANT_ORDER_STATUSES];

  /** Šema boja ili tema organizacije (`org:<id>`, v. OrgThemeService). ⚠ SYNC: podrazumevana vrednost =
   *  ThemeService default (konobar i POS ne zadaju DEFAULT_APP_THEME → plum) = pre-boot skripta u
   *  index.html oba app-a. */
  theme: ThemeSetting = 'plum';
}

/** Vrednost svakog ključa kao JSON — za poređenje dva stanja podešavanja (koji ključevi su promenjeni). */
function settingsSnapshot(value: Settings): Record<string, string> {
  const snapshot: Record<string, string> = {};
  for (const key of Object.keys(value)) {
    snapshot[key] = JSON.stringify((value as any)[key]);
  }
  return snapshot;
}

/** Podešavanja uređaja koja, dok su null, preuzimaju vrednost sa servera (CompanySettings). */
export type ServerBackedSetting =
  | 'showOrderNoOnCreate'
  | 'repeatSoundForPendingOrders'
  | 'showPaidInstantOrders';

/**
 * Serverska vrednost za svako takvo podešavanje — sa istim smislom koji je važio pre podešavanja
 * na uređaju (npr. naplaćene se kriju samo kad je na serveru eksplicitno false).
 */
export const SERVER_SETTING: Record<ServerBackedSetting, (cs: CompanySettings | null | undefined) => boolean> = {
  showOrderNoOnCreate: cs => cs?.showOrderNoOnWaiterApp === true,
  repeatSoundForPendingOrders: cs => cs?.repeatSoundForPendingNotifications === true,
  showPaidInstantOrders: cs => cs?.showPayedInstantOrders !== false,
};

/** Snapshot poslednjeg primenjenog profila (localStorage "appliedProfile"). */
export interface AppliedProfile {
  profileID: number;
  version: number;
  name: string;
  settings: Partial<Settings>;
}

/**
 * Efektivna podešavanja = defaults (Settings klasa) → profil → lokalni override-i.
 * Profil je DEFAULT: izmena na samom uređaju se čuva kao sparse diff ("settingsOverrides")
 * koji pobeđuje profil i preživljava nove verzije profila. Bez profila ponašanje je
 * identično legacy putu (localStorage "appSettings"). Isti model kao kiosk SettingsService.
 */
@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  current = new Settings();

  /** Signal promene podešavanja → (debounce) "Settings changed" u logu, v. logSettings(). */
  private readonly changed$ = new Subject<void>();

  /** Podešavanja su promenjena (ekran, admin portal, profil) — `current` već ima nove vrednosti. */
  readonly changes = this.changed$.asObservable();

  constructor(private ws: WSService, private taxCore: TaxCoreService,
    private themeService: ThemeService,
    private orgTheme: OrgThemeService,
    private coreSession: CoreSessionService,
    private logger: NGXLogger) {
    this.load();

    ws.observe("settings_get").subscribe(
      (request) => {

        var oldSettings = localStorage.getItem("settings");

        var response: WebsocketData = {
          type: "response",
          requestID: request.requestID,
          data: {
            settings: this.current,
            oldSettings: oldSettings
          }
        };

        ws.send(response);
      });
  }

  /** Aktivni profil (null = bez profila → legacy lokalna podešavanja). */
  get appliedProfile(): AppliedProfile | null {
    try {
      const raw = localStorage.getItem("appliedProfile");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private get overrides(): Partial<Settings> {
    try {
      const raw = localStorage.getItem("settingsOverrides");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  /** Efektivna vrednost podešavanja koje se nasleđuje sa servera: izbor na uređaju, a dok ga
   *  nema (null) — podešavanje firme sa servera. */
  resolve(key: ServerBackedSetting, companySettings: CompanySettings | null | undefined): boolean {
    return this.current[key] ?? SERVER_SETTING[key](companySettings);
  }

  /** Ima li lokalnih izmena jačih od profila (za prikaz na settings ekranu). */
  get hasOverrides(): boolean {
    return Object.keys(this.overrides).length > 0;
  }

  save(val?: Settings) {
    if (!val) {
      val = this.current;
    }

    const profile = this.appliedProfile;
    if (profile) {
      // Sa aktivnim profilom čuvamo SAMO razliku u odnosu na (defaults + profil) — lokalne
      // override-e. Legacy "appSettings" ne diramo: ostaje fallback ako se profil skine.
      const base = Object.assign(new Settings(), profile.settings || {});
      const diff: Partial<Settings> = {};
      for (const key of Object.keys(val) as (keyof Settings)[]) {
        if (JSON.stringify(val[key]) !== JSON.stringify(base[key])) {
          (diff as any)[key] = val[key];
        }
      }
      if (Object.keys(diff).length) {
        localStorage.setItem("settingsOverrides", JSON.stringify(diff));
      } else {
        localStorage.removeItem("settingsOverrides");
      }
    } else {
      var s = JSON.stringify(val);
      localStorage.setItem("appSettings", s);
    }

    Object.assign(this.current, val);
    this.applyCurrent();
  }

  /**
   * Primena profila sa servera (boot / WS 'deviceProfile.changed' / picker).
   * Idempotentno po (profileID, version); reset=true (push sa "resetuj lokalna podešavanja")
   * briše override-e pre primene. payload=null → profil skinut: nazad na legacy podešavanja.
   */
  applyProfile(payload: DeviceProfilePayload | null, reset = false) {
    if (reset) {
      localStorage.removeItem("settingsOverrides");
    }

    if (!payload) {
      const hadProfile = !!this.appliedProfile;
      localStorage.removeItem("appliedProfile");
      if (hadProfile || reset) {
        this.reloadEffective();
      }
      return;
    }

    const current = this.appliedProfile;
    if (current && current.profileID === payload.profileID && current.version === payload.version && !reset) {
      return;
    }

    const snapshot: AppliedProfile = {
      profileID: payload.profileID,
      version: payload.version,
      name: payload.name,
      settings: payload.settings || {},
    };
    localStorage.setItem("appliedProfile", JSON.stringify(snapshot));
    this.reloadEffective();
  }

  /** "Vrati na profil": briše lokalne izmene — podešavanja postaju tačno profil + defaults. */
  clearOverrides() {
    localStorage.removeItem("settingsOverrides");
    this.reloadEffective();
  }

  private reloadEffective() {
    // `current` je deljena referenca po celoj aplikaciji — mutira se u mestu.
    Object.assign(this.current, this.computeEffective());
    this.applyCurrent();
  }

  /** Posledice podešavanja van ovog servisa: VPFR fiskalizacija, log pinga i tema (ThemeService je ne čuva sam). */
  private applyCurrent() {
    this.taxCore.vpfrEnabled = this.current.generateInvoiceWithVPFR;
    this.ws.logPing = this.current.logPing;

    // Tema: ugrađena šema ili tema organizacije (`org:<id>`, šema + CSS iz kataloga u company
    // podešavanjima; dok katalog ne stigne važi keširana šema teme). applySelection je idempotentan —
    // bitno, jer ekran podešavanja snima na svaki taster, a set('auto') svaki put računa sunce i
    // tajmer. Nov katalog primenjuje app (pretplata na company podešavanja u app.component).
    this.orgTheme.applySelection(
      this.current.theme,
      this.coreSession.companySettings.value?.organizationCssThemes,
      this.coreSession.companyID
    );

    this.changed$.next();
  }

  private computeEffective(): Settings {
    var appJSettings = new Settings();

    const profile = this.appliedProfile;
    if (profile) {
      Object.assign(appJSettings, profile.settings || {}, this.overrides);
      return appJSettings;
    }

    var appSettings = localStorage.getItem("appSettings");

    if (appSettings) {
      var jSettings = JSON.parse(appSettings);
      Object.assign(appJSettings, jSettings);
    }
    else {
      var oldSettings = localStorage.getItem("settings") || "{}";

      if (oldSettings) {
        var jOldSettings = JSON.parse(oldSettings);
        Object.assign(appJSettings, jOldSettings);
        appJSettings.useGridTableView = false;
        appJSettings.layoutTableView = 'cover';
      }
    }

    return appJSettings;
  }

  private load() {
    Object.assign(this.current, this.computeEffective());

    // Tema je ranije bila zaseban ključ core ThemeService-a ('appTheme'), van podešavanja
    // uređaja — zatečeni izbor prelazi u settings (sa profilom: lokalna izmena, ako se
    // razlikuje); takeLegacyTheme() pri tom briše stari ključ.
    const legacyTheme = this.themeService.takeLegacyTheme();
    if (legacyTheme) {
      this.current.theme = legacyTheme;
      this.save();
    }

    this.applyCurrent();
    this.logSettings();
  }

  /**
   * Kompletna efektivna podešavanja u log — odmah pri učitavanju, pa posle svake promene
   * (ekran podešavanja, profil sa servera, "Vrati na profil"), da se iz loga uređaja vidi sa
   * kojim podešavanjima je app radio.
   *
   * Promene idu kroz debounce: ekran podešavanja snima na SVAKU izmenu forme (svaki taster),
   * pa bi bez njega svaki taster upisao ceo settings.
   */
  private logSettings() {
    let logged = settingsSnapshot(this.current);
    let loggedSource = JSON.stringify(this.settingsSource);
    this.logger.info("Settings loaded", { ...this.settingsSource, settings: this.current });

    this.changed$.pipe(debounceTime(1500)).subscribe(() => {
      const snapshot = settingsSnapshot(this.current);
      const changed = Object.keys({ ...logged, ...snapshot }).filter(key => snapshot[key] !== logged[key]);
      const source = this.settingsSource;
      const sourceJson = JSON.stringify(source);

      // ponovno snimanje istih vrednosti nije promena
      if (!changed.length && sourceJson === loggedSource) {
        return;
      }

      logged = snapshot;
      loggedSource = sourceJson;
      this.logger.info("Settings changed: " + (changed.join(", ") || "profile only"), { ...source, settings: this.current });
    });
  }

  /** Odakle dolaze efektivna podešavanja (profil + lokalne izmene) — ide uz svaki log podešavanja. */
  private get settingsSource() {
    const profile = this.appliedProfile;
    return {
      profile: profile ? { profileID: profile.profileID, version: profile.version, name: profile.name } : null,
      localOverrides: profile ? Object.keys(this.overrides) : null,
    };
  }
}
